import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import type { TrendMonth } from '@/models/salesAnalytics'
import { monthLabel, MonthlyTrendCard, type TrendMetric } from './MonthlyTrendCard'

const STAGES: TrendMetric[] = [
  { key: 'leads', label: 'Leads' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'applicationViewed', label: 'App viewed' },
  { key: 'applicationCompleted', label: 'App completed' },
  { key: 'quoting', label: 'Quoting' },
  { key: 'quoteSent', label: 'Quote sent' },
  { key: 'closedWon', label: 'Closed won' },
]

const ACTIVITY: TrendMetric[] = [
  { key: 'outreach', label: 'Outreach' },
  { key: 'callsOut', label: 'Calls out' },
  { key: 'callsMissed', label: 'Missed' },
  { key: 'callsIn', label: 'Calls in' },
  { key: 'emailsOut', label: 'Emails out' },
  { key: 'smsOut', label: 'SMS out' },
  { key: 'callsPerApplication', label: 'Calls / app', ratio: true, format: (v) => v.toFixed(1) },
  { key: 'outreachPerApplication', label: 'Outreach / app', ratio: true, format: (v) => v.toFixed(0) },
]

/** Every stage for every month, so a drop can be traced down the funnel. */
function FunnelByMonthTable({ months }: { months: TrendMonth[] }) {
  return (
    <details className="text-sm">
      <summary className="cursor-pointer text-neutral-500">Every stage, month by month</summary>
      <div className="mt-2 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              {STAGES.map((stage) => (
                <TableHead key={stage.key} className="text-right">
                  {stage.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {months.map((month) => (
              <TableRow key={month.month}>
                <TableCell>{monthLabel(month.month)}</TableCell>
                {STAGES.map((stage) => (
                  <TableCell key={stage.key} className="text-right tabular-nums">
                    {month[stage.key]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </details>
  )
}

export function FunnelTrendCard({ months, seriesLabel }: { months: TrendMonth[]; seriesLabel: string }) {
  return (
    <MonthlyTrendCard
      title="Funnel by month"
      description="The funnel above, month by month: leads counted in the month they first reached each stage, so a month shows what moved then. Leads is new leads created. Follows the date, rep and source filters."
      months={months}
      metrics={STAGES}
      seriesLabel={seriesLabel}
    >
      <FunnelByMonthTable months={months} />
    </MonthlyTrendCard>
  )
}

export function ActivityTrendCard({ months, seriesLabel }: { months: TrendMonth[]; seriesLabel: string }) {
  return (
    <MonthlyTrendCard
      title="Sales activity by month"
      description="The Activity by rep table below, month by month: a month adds up that table's columns for the month. Per-app ratios divide by leads that reached App viewed that month."
      months={months}
      metrics={ACTIVITY}
      seriesLabel={seriesLabel}
    />
  )
}
